import { Capacitor } from '@capacitor/core';
import { fetchAuthMeta } from '../auth-api';
import { openNativeOAuthLogin } from '../native-oauth';

const VALID_PROVIDERS = new Set(['google', 'discord', 'tiktok', 'telegram']);

export function isValidOAuthProvider(provider: string) {
  return VALID_PROVIDERS.has(provider);
}

/** Web OAuth returns to /redirect/ so a minimal handoff can skip the splash cinematic. */
export const OAUTH_REDIRECT_PATH = '/redirect/';

const OAUTH_RETURN_FLAG = 'booga_oauth_return';
const OAUTH_TOKEN_KEY = 'booga_oauth_token';

export function isOAuthRedirectReturn() {
  try {
    if (sessionStorage.getItem(OAUTH_RETURN_FLAG) === '1') return true;
  } catch {
    /* ignore */
  }
  const path = (window.location.pathname.replace(/\/+$/, '') || '/') + '/';
  return path === OAUTH_REDIRECT_PATH || path.endsWith('/redirect/');
}

export function consumeOAuthReturnFlag() {
  try {
    sessionStorage.removeItem(OAUTH_RETURN_FLAG);
  } catch {
    /* ignore */
  }
}

/** True when the browser is still on the OAuth callback path. */
export function isOnOAuthRedirectPath() {
  const path = (window.location.pathname.replace(/\/+$/, '') || '/') + '/';
  return path === OAUTH_REDIRECT_PATH || path.endsWith('/redirect/');
}

/**
 * After a successful OAuth session exchange, put the address bar on the app root
 * (not /redirect). Soft replace — no reload — so splash is not replayed.
 */
export function navigateToAppRootAfterOAuth() {
  if (!isOnOAuthRedirectPath() && !window.location.search.includes('token=')) {
    return false;
  }
  history.replaceState(null, '', `${window.location.origin}/`);
  return true;
}

/** Token stashed by /redirect/index.html before hopping back to the app root. */
export function consumeOAuthTokenFromStorage() {
  try {
    const token = sessionStorage.getItem(OAUTH_TOKEN_KEY);
    sessionStorage.removeItem(OAUTH_TOKEN_KEY);
    return token;
  } catch {
    return null;
  }
}

export function getOAuthRedirectUrl() {
  if (Capacitor.isNativePlatform()) {
    return 'com.booga.smash://oauth';
  }
  return `${window.location.origin}${OAUTH_REDIRECT_PATH}`;
}

export async function gaupaDevnetLoginUri(provider: string, meta?: { gaupaHost: string; publicKey: string }) {
  if (!isValidOAuthProvider(provider)) return null;

  const { gaupaHost, publicKey } = meta ?? (await fetchAuthMeta());
  if (!gaupaHost || !publicKey) return null;

  const base = gaupaHost.replace(/\/$/, '');
  const url = new URL('/login', base);
  url.searchParams.set('public', publicKey);
  url.searchParams.set('redirect', getOAuthRedirectUrl());
  url.searchParams.set('provider', provider);
  return url;
}

/** Redirect the current tab or native browser to Gaupa OAuth. */
export async function startOAuthLogin(provider: string): Promise<string | null> {
  if (!isValidOAuthProvider(provider)) {
    return 'Invalid provider';
  }

  let meta: { gaupaHost: string; publicKey: string };
  try {
    meta = await fetchAuthMeta();
  } catch {
    return 'Could not load auth configuration';
  }

  const uri = await gaupaDevnetLoginUri(provider, meta);
  if (!uri) return 'Could not build login URL';

  if (Capacitor.isNativePlatform()) {
    await openNativeOAuthLogin(uri.toString());
    return null;
  }

  window.location.href = uri.toString();
  return null;
}
